import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Play, Instagram, Globe, Share2 } from "lucide-react";
import { mediaUrl, api, userApi } from "../lib/api";
import { useAudio } from "../store/audio";

const ease = [0.16, 1, 0.3, 1];
const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-70px" }} transition={{ duration: 0.9, ease, delay }} className={className}>
    {children}
  </motion.div>
);

const Section = ({ id, children, light, className = "" }) => (
  <section id={id} className={`border-t eff-line ${light ? "bg-[#f2f2ef] text-[#111]" : ""} ${className}`}>
    <div className={`py-[80px] md:py-[100px] ${light ? "text-center px-5" : ""}`}>{children}</div>
  </section>
);

const Label = ({ children, light }) => (
  <div className={`eff-label mb-3.5 ${light ? "!text-[#666]" : ""}`}>{children}</div>
);
const H2 = ({ children, className = "" }) => (
  <h2 className={`font-display text-[34px] md:text-[46px] leading-[1.1] mb-5 ${className}`}>{children}</h2>
);

/* ---------------------------------- HERO --------------------------------- */
export const Hero = ({ settings }) => {
  const h = settings.hero || {};
  const logo = settings.logo_svg;
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" && window.innerWidth < 768);
  useEffect(() => {
    const on = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);

  let kind = "black", src = "", poster = h.poster;
  if (settings.identity_video) {
    kind = "video"; src = settings.identity_video;
    poster = settings.identity_video_poster || h.poster;
  } else if (h.mode === "video") {
    if (isMobile) {
      if (h.video_mobile) { kind = "video"; src = h.video_mobile; }
      else if (h.fallback_image) { kind = "image"; src = h.fallback_image; }
      else if (h.video_url) { kind = "video"; src = h.video_url; }
    } else if (h.video_url) { kind = "video"; src = h.video_url; }
  } else {
    if (isMobile && h.image_mobile) { kind = "image"; src = h.image_mobile; }
    else if (h.image_desktop) { kind = "image"; src = h.image_desktop; }
  }
  return (
    <header id="hero" className="relative min-h-[650px] md:min-h-[760px] flex items-center justify-center text-center overflow-hidden">
      {kind === "video" && (
        <video key={src} data-testid="hero-video" className="absolute inset-0 w-full h-full object-cover"
          autoPlay={h.autoplay !== false} muted={h.muted !== false} loop={h.loop !== false} playsInline poster={mediaUrl(poster)}>
          <source src={mediaUrl(src)} />
        </video>
      )}
      {kind === "image" && <img data-testid="hero-image" src={mediaUrl(src)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      {kind !== "black" && <div className="absolute inset-0 bg-black/70" />}
      <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1.1, ease }}
        className="relative max-w-[1180px] px-7 w-full">
        <div className="eff-eyebrow mb-6">{settings.tagline}</div>
        {logo ? (
          <img data-testid="hero-logo" src={mediaUrl(logo)} alt={settings.brand}
            className="w-[min(560px,88vw)] h-auto block mx-auto mb-9 mix-blend-screen" />
        ) : (
          <h1 data-testid="hero-title" className="font-display text-5xl md:text-7xl mb-9">{settings.brand}</h1>
        )}
        <p className="max-w-[680px] mx-auto text-[#b5b5b5] leading-[1.85] text-[15px] md:text-[17px]">
          {(settings.hero || {}).subtitle || settings.central_message}
        </p>
        <div className="flex justify-center gap-3 flex-wrap mt-9">
          <a data-testid="hero-cta" className="eff-btn eff-btn-light" href="#univers">{h.cta_label || "Découvrir"}</a>
          <a className="eff-btn" href="#collaborer">Proposer un projet</a>
        </div>
      </motion.div>
    </header>
  );
};

/* -------------------------------- MANIFESTE ------------------------------- */
export const Manifeste = ({ settings }) => {
  const m = settings.manifesto || {};
  return (
    <Section id="manifeste">
      <Reveal><Label>00 · Manifeste</Label></Reveal>
      <Reveal><H2>{m.text}</H2></Reveal>
      {m.subtext && <Reveal delay={0.08}><p className="intro max-w-[720px] text-[#a6a6a6] leading-[1.85]">{m.subtext}</p></Reveal>}
    </Section>
  );
};

/* --------------------------------- UNIVERS -------------------------------- */
export const Univers = ({ items = [] }) => (
  <Section id="univers">
    <Reveal><Label>01 · Notre univers</Label></Reveal>
    <Reveal><H2>Plus qu'un événement.</H2></Reveal>
    <Reveal delay={0.05}>
      <p className="max-w-[720px] text-[#a6a6a6] leading-[1.85]">
        EFFERVESCENCE crée ses propres concepts tout en ouvrant ses espaces et ses ressources à celles et ceux qui souhaitent partager une idée, un talent, une œuvre ou une expérience.
      </p>
    </Reveal>
    <div className="grid md:grid-cols-3 gap-px bg-[#292929] mt-12">
      {items.map((it, i) => (
        <Reveal key={it.id} delay={(i % 3) * 0.05}>
          <div data-testid={`universe-${i}`} className="bg-[#080808] p-8 min-h-[205px] h-full group hover:bg-[#0d0d0d] transition-colors duration-500">
            <span className="text-[10px] text-[#aaa] tracking-[0.2em]">{it.number}</span>
            <h3 className="font-display text-[24px] mt-9 mb-2.5">{it.title}</h3>
            <p className="text-[13px] leading-[1.7] text-[#929292]">{it.description}</p>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

/* ------------------------------- EXPERIENCES ------------------------------ */
export const Experiences = ({ items = [] }) => (
  <Section id="experiences">
    <Reveal><Label>· Expériences</Label></Reveal>
    <Reveal><H2>Concepts & créations.</H2></Reveal>
    <div className="grid md:grid-cols-3 gap-px bg-[#292929] mt-12">
      {items.map((it, i) => (
        <Reveal key={it.id} delay={(i % 3) * 0.05}>
          <div data-testid={`experience-${i}`} className="bg-[#080808] group">
            <div className="overflow-hidden">
              <img src={mediaUrl(it.media_url)} alt="" className="w-full h-56 object-cover grayscale group-hover:grayscale-0 transition-all duration-[900ms]" />
            </div>
            <div className="p-7">
              <h3 className="font-display text-[22px] mb-2">{it.caption}</h3>
              <p className="text-[13px] leading-[1.7] text-[#929292]">{it.description}</p>
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

/* -------------------------------- EVENEMENTS ------------------------------ */
const buyTicket = async (ev) => {
  try {
    const { data } = await userApi.post("/payments/checkout/ticket", { event_id: ev.id, quantity: 1, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  } catch (e) {
    if (e.response?.status === 401) { window.location.href = "/access"; return; }
    toast.error(e.response?.data?.detail || "Billetterie indisponible");
  }
};

export const Evenements = ({ items = [] }) => (
  <Section id="evenements">
    <Reveal><Label>· Événements</Label></Reveal>
    <Reveal><H2>À venir.</H2></Reveal>
    <div className="mt-12 border-t eff-line">
      {items.map((it, i) => (
        <Reveal key={it.id} delay={(i % 3) * 0.05}>
          <div data-testid={`event-${i}`} className="border-b eff-line py-7 grid md:grid-cols-12 gap-6 items-center group hover:bg-[#0d0d0d] transition-colors duration-500 px-1">
            {it.image && (
              <div className="md:col-span-3 overflow-hidden">
                <img src={mediaUrl(it.image)} alt="" className="w-full h-32 object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
              </div>
            )}
            <div className="md:col-span-6">
              <div className="text-[10px] tracking-[0.2em] uppercase text-[#aaa]">{it.category} · {it.timeframe === "past" ? "Passé" : "À venir"}</div>
              <h3 className="font-display text-[24px] mt-2">{it.title}</h3>
              <p className="text-[13px] leading-[1.7] text-[#929292] mt-1.5">{it.description}</p>
            </div>
            <div className="md:col-span-3 text-[13px] text-[#929292] md:text-right">
              <div>{it.date}{it.time ? ` · ${it.time}` : ""}</div>
              <div>{it.location}</div>
              {it.external_link && <a href={it.external_link} target="_blank" rel="noreferrer" className="inline-block mt-2 text-white hover:underline">Détails →</a>}
              {(it.ticketing_enabled === true || String(it.ticketing_enabled) === "true") && (
                (it.ticket_quota > 0 && (it.tickets_sold || 0) >= it.ticket_quota)
                  ? <div className="mt-3 text-[10px] tracking-widest uppercase text-[#777]">Complet</div>
                  : <button data-testid={`event-buy-${i}`} onClick={() => buyTicket(it)} className="eff-btn mt-3 inline-block">Billet · {Number(it.ticket_price || 0).toFixed(2)} $</button>
              )}
            </div>
          </div>
        </Reveal>
      ))}
    </div>
  </Section>
);

/* ---------------------------------- SOUND --------------------------------- */
export const Sound = ({ tracks = [], playlists = [], deepTrack = "", deepPlaylist = "" }) => {
  const playList = useAudio((s) => s.playList);
  const currentId = useAudio((s) => s.current()?.id);
  const pubPlaylists = (playlists || []).filter((p) => (p.track_ids || []).length > 0);
  const [tab, setTab] = useState("all");

  const listFor = (t) => {
    if (t === "all") return tracks;
    const pl = pubPlaylists.find((p) => p.id === t);
    if (!pl) return tracks;
    return (pl.track_ids || []).map((id) => tracks.find((x) => x.id === id)).filter(Boolean);
  };
  const list = listFor(tab);

  useEffect(() => {
    if (deepPlaylist && pubPlaylists.some((p) => p.id === deepPlaylist)) {
      setTab(deepPlaylist);
      setTimeout(() => document.getElementById("sound")?.scrollIntoView({ behavior: "smooth" }), 300);
    }
    if (deepTrack) {
      const idx = tracks.findIndex((t) => t.id === deepTrack);
      if (idx !== -1) {
        playList(tracks, idx);
        setTimeout(() => document.getElementById("sound")?.scrollIntoView({ behavior: "smooth" }), 300);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepTrack, deepPlaylist, tracks.length]);

  const share = (e, path) => {
    e.stopPropagation();
    navigator.clipboard.writeText(`${window.location.origin}/${path}`);
    toast.success("Lien copié");
  };

  return (
    <Section id="sound">
      <div className="flex items-end justify-between flex-wrap gap-5">
        <div>
          <Reveal><Label>· EFFERVESCENCE Sound</Label></Reveal>
          <Reveal><H2 className="!mb-0">Le son du collectif.</H2></Reveal>
        </div>
        {list.length > 0 && (
          <Reveal>
            <div className="flex items-center gap-3">
              <button data-testid="sound-play-all" onClick={() => playList(list, 0)} className="eff-btn inline-flex items-center gap-2">
                <Play className="w-3.5 h-3.5" /> Tout écouter
              </button>
              {tab !== "all" && (
                <button data-testid="sound-share-playlist" onClick={(e) => share(e, `?playlist=${tab}`)} className="eff-btn inline-flex items-center gap-2">
                  <Share2 className="w-3.5 h-3.5" /> Partager
                </button>
              )}
            </div>
          </Reveal>
        )}
      </div>

      {pubPlaylists.length > 0 && (
        <Reveal delay={0.05}>
          <div className="flex flex-wrap gap-6 mt-8 border-b eff-line pb-4">
            <button data-testid="sound-tab-all" onClick={() => setTab("all")}
              className={`text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${tab === "all" ? "text-white" : "text-[#777] hover:text-white"}`}>
              Tout ({tracks.length})
            </button>
            {pubPlaylists.map((p) => (
              <button key={p.id} data-testid={`sound-tab-${p.id}`} onClick={() => setTab(p.id)}
                className={`text-[10px] tracking-[0.2em] uppercase transition-colors duration-300 ${tab === p.id ? "text-white" : "text-[#777] hover:text-white"}`}>
                {p.title} ({(p.track_ids || []).length})
              </button>
            ))}
          </div>
        </Reveal>
      )}

      <div className={`${pubPlaylists.length > 0 ? "mt-2" : "mt-12"} border-t eff-line`}>
        {list.map((t, i) => (
          <Reveal key={t.id} delay={(i % 4) * 0.04}>
            <div data-testid={`sound-track-${i}`}
              className={`group w-full border-b eff-line py-4 grid grid-cols-12 gap-4 items-center px-1 transition-colors duration-300 ${currentId === t.id ? "bg-[#111]" : "hover:bg-[#0d0d0d]"}`}>
              <button onClick={() => playList(list, i)} className="col-span-8 md:col-span-9 text-left grid grid-cols-8 md:grid-cols-9 gap-4 items-center">
                <div className="col-span-1 text-[#929292] text-[13px]">{String(i + 1).padStart(2, "0")}</div>
                <div className="col-span-1">{t.cover && <img src={mediaUrl(t.cover)} alt="" className="w-10 h-10 object-cover border eff-line" />}</div>
                <div className="col-span-6 md:col-span-7">
                  <div className="text-[15px]">{t.title}</div>
                  <div className="text-[12px] text-[#929292]">{t.artist}</div>
                </div>
              </button>
              <div className="col-span-4 md:col-span-3 flex items-center justify-end gap-4">
                <span className="text-[9px] tracking-[0.2em] uppercase text-[#aaa] hidden sm:inline">{t.source_type === "embed" ? `Embed · ${t.embed_platform}` : "Owned"}</span>
                <button data-testid={`sound-share-${i}`} onClick={(e) => share(e, `?track=${t.id}`)} className="text-[#777] hover:text-white transition-colors duration-300" title="Copier le lien">
                  <Share2 className="w-4 h-4" strokeWidth={1.3} />
                </button>
              </div>
            </div>
          </Reveal>
        ))}
        {list.length === 0 && <p className="text-[#929292] text-sm py-6">Aucune piste.</p>}
      </div>
    </Section>
  );
};

/* ------------------------------- COLLABORATEURS --------------------------- */
const Profile = ({ p, testid }) => (
  <div data-testid={testid} className="group">
    <div className="overflow-hidden border eff-line">
      {p.photo && <img src={mediaUrl(p.photo)} alt="" className="w-full h-72 object-cover grayscale group-hover:grayscale-0 transition-all duration-[900ms]" />}
    </div>
    <div className="mt-4">
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#aaa]">{p.discipline}</div>
      <h3 className="font-display text-[22px] mt-1">{p.name}</h3>
      <p className="text-[13px] leading-[1.7] text-[#929292] mt-1.5">{p.description}</p>
      <div className="flex gap-4 mt-3 text-[#aaa]">
        {p.instagram && <a href={p.instagram} target="_blank" rel="noreferrer" className="hover:text-white"><Instagram className="w-4 h-4" strokeWidth={1.3} /></a>}
        {p.website && <a href={p.website} target="_blank" rel="noreferrer" className="hover:text-white"><Globe className="w-4 h-4" strokeWidth={1.3} /></a>}
      </div>
    </div>
  </div>
);

export const People = ({ artists = [], collaborators = [] }) => (
  <Section id="collaborateurs">
    <Reveal><Label>· Collaborateurs & artistes</Label></Reveal>
    <Reveal><H2>Le réseau.</H2></Reveal>
    {artists.length > 0 && (
      <>
        <div className="eff-label mt-10 mb-6">Artistes</div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-7 md:gap-10">
          {artists.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 0.05}><Profile p={p} testid={`artist-${i}`} /></Reveal>)}
        </div>
      </>
    )}
    {collaborators.length > 0 && (
      <>
        <div className="eff-label mt-16 mb-6">Collaborateurs</div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-7 md:gap-10">
          {collaborators.map((p, i) => <Reveal key={p.id} delay={(i % 3) * 0.05}><Profile p={p} testid={`collaborator-${i}`} /></Reveal>)}
        </div>
      </>
    )}
  </Section>
);

/* -------------------------------- COLLABORER ------------------------------ */
export const Collaborer = ({ settings }) => {
  const c = settings.collaborate || {};
  const [form, setForm] = useState({ name: "", email: "", phone: "", project_name: "", type: "", description: "", needs: "", social: "" });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault(); setLoading(true); setStatus(null);
    try {
      await api.post("/public/submissions", form);
      setStatus("ok");
      setForm({ name: "", email: "", phone: "", project_name: "", type: "", description: "", needs: "", social: "" });
    } catch { setStatus("error"); } finally { setLoading(false); }
  };
  const field = "w-full bg-transparent border-b eff-line py-2.5 text-[13px] placeholder:text-[#5a5a5a] focus:outline-none focus:border-white transition-colors duration-300";
  return (
    <Section id="collaborer">
      <div className="grid md:grid-cols-2 gap-14 md:gap-20 items-start">
        <div>
          <Reveal><Label>02 · {c.eyebrow || "Collaborer"}</Label></Reveal>
          <Reveal><H2>{c.title}</H2></Reveal>
          <Reveal delay={0.05}><p className="text-[#a6a6a6] leading-[1.85] max-w-[560px]">{c.text}</p></Reveal>
          <Reveal delay={0.1}>
            <div className="border-l border-[#aaa] pl-7 text-[#ccc] leading-[1.9] mt-10 text-[14px]">
              Selon le projet, nous pouvons mettre à disposition <strong className="text-white font-normal">nos espaces, notre logistique, notre réseau et notre savoir-faire événementiel</strong> afin de donner aux créateurs les moyens de présenter leur univers.
            </div>
          </Reveal>
        </div>
        <Reveal delay={0.05}>
          <form data-testid="collaborate-form" onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-x-7 gap-y-5">
            <input data-testid="cf-name" className={field} placeholder="Nom *" required value={form.name} onChange={set("name")} />
            <input data-testid="cf-email" type="email" className={field} placeholder="Email *" required value={form.email} onChange={set("email")} />
            <input data-testid="cf-phone" className={field} placeholder="Téléphone" value={form.phone} onChange={set("phone")} />
            <input data-testid="cf-project" className={field} placeholder="Nom du projet" value={form.project_name} onChange={set("project_name")} />
            <input data-testid="cf-type" className={field} placeholder="Type de projet" value={form.type} onChange={set("type")} />
            <input data-testid="cf-social" className={field} placeholder="Instagram / site" value={form.social} onChange={set("social")} />
            <textarea data-testid="cf-desc" className={`${field} sm:col-span-2 resize-none`} rows={3} placeholder="Description *" required value={form.description} onChange={set("description")} />
            <textarea data-testid="cf-needs" className={`${field} sm:col-span-2 resize-none`} rows={2} placeholder="Vos besoins" value={form.needs} onChange={set("needs")} />
            <div className="sm:col-span-2 flex items-center gap-5 mt-3">
              <button data-testid="cf-submit" disabled={loading} className="eff-btn eff-btn-light disabled:opacity-40">
                {loading ? "Envoi..." : "Proposer un projet"}
              </button>
              {status === "ok" && <span data-testid="cf-success" className="text-[12px] text-white">Merci — votre proposition a été envoyée.</span>}
              {status === "error" && <span data-testid="cf-error" className="text-[12px] text-[#a6a6a6]">Une erreur est survenue.</span>}
            </div>
          </form>
        </Reveal>
      </div>
    </Section>
  );
};

/* --------------------------------- CONTACT -------------------------------- */
export const Contact = ({ settings }) => {
  const ct = settings.contact || {};
  return (
    <Section id="contact" light>
      <Reveal><Label light>03 · Rejoindre l'effervescence</Label></Reveal>
      <Reveal><H2 className="!text-[#111] mx-auto">Créons quelque chose.</H2></Reveal>
      <Reveal delay={0.05}>
        <p className="max-w-[620px] mx-auto text-[#555] leading-[1.7] mb-8">
          Artiste, créateur, entrepreneur, marque, collectif ou porteur de projet : présentez-nous votre idée.
        </p>
      </Reveal>
      <Reveal delay={0.1}>
        <a data-testid="contact-email" className="eff-btn eff-btn-dark" href={`mailto:${ct.email}`}>Nous contacter</a>
      </Reveal>
      <Reveal delay={0.15}>
        <div className="flex justify-center gap-7 mt-8">
          {(settings.socials || []).map((s, i) => (
            <a key={i} href={s.url} target="_blank" rel="noreferrer" className="text-[10px] tracking-[0.2em] uppercase text-[#666] hover:text-[#111] transition-colors">{s.platform}</a>
          ))}
        </div>
      </Reveal>
      <Reveal delay={0.2}><div className="text-[11px] text-[#777] mt-6">{ct.email} · {ct.address}{ct.phone ? ` · ${ct.phone}` : ""}</div></Reveal>
    </Section>
  );
};

/* ---------------------------------- FOOTER -------------------------------- */
export const Footer = ({ settings }) => {
  const f = settings.footer || {};
  return (
    <footer className="border-t eff-line py-8 flex justify-between gap-5 flex-wrap text-[9px] tracking-[0.16em] uppercase text-[#777]">
      <span>{settings.brand}</span>
      <span>{f.note || "Culture · Créativité · Expériences"}</span>
      <span>© {new Date().getFullYear()}</span>
    </footer>
  );
};
